import dynamic from 'next/dynamic';
import type { MouseEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PopupActions } from '~components/Popup/types';
import type { TMedia } from '~constants/media';
import { useBotPaywallContext } from '~context/botPaywall';
import { ReportMessageSource, useReportActionsContext } from '~context/report';
import type { TBotConfigExtended } from '~entities/bot';
import { annals } from '~features/annals/hooks';
import { Topic, usePremiumTopics, useTopics } from '~features/topics';
import { useMessageProcessing } from '~hooks/useMessageProcessing';
import { useSession } from '~hooks/useSession';
import { getMessageType } from '~utils/messages';
import { transformMessagePaymentType } from '~utils/payments';

import type { TMessage } from '../../model/types';
import { PaidImageMessage } from '../PaidImageMessage';
import { PaidTextMessage } from '../PaidTextMessage';
import { RegenerateButton } from '../RegenerateButton';
import { ReportMessage } from '../ReportMessage';
import { ReportPopup } from '../ReportPopup';
import { SextingPackMessage } from '../SextingPackMessage';
import { Message } from '../styles';
import { TextMessage } from '../TextMessage';
import { TopicContainer } from './styles';

const AudioMessageComponent = dynamic(
	() =>
		import('../AudioMessage').then((module) => module.AudioMessageComponent),
	{
		ssr: false,
		loading: () => <div style={{ height: '40px' }}> </div>,
	}
);
const ImageMessage = dynamic(
	() => import('../ImageMessage').then((module) => module.ImageMessage),
	{
		ssr: false,
		loading: () => <div style={{ height: '160px' }}></div>,
	}
);

const GiftMessage = dynamic(
	() => import('../GiftImageMessage').then((module) => module.GiftMessage),
	{
		ssr: false,
		loading: () => <div style={{ height: '160px' }}></div>,
	}
);
const VideoMessage = dynamic(
	() => import('../VideoMessage').then((module) => module.VideoMessage),
	{
		ssr: false,
		loading: () => <div style={{ height: '200px' }}></div>,
	}
);

export type Props = {
	message: TMessage;
	userId: string;
	bot: TBotConfigExtended;
	showTopic?: boolean;
	isLast: boolean;
	showWarning: boolean;
};

const MessageItem = ({
	message,
	userId,
	bot,
	isLast,
	// showWarning,
	showTopic,
}: Props) => {
	const isMessageFromInfluencer = bot.accountType === 'influencer';

	const botId = bot.clientId.toString();

	const { id: messageId, sender, meta } = message;
	const cost = meta?.tariffication?.cost ?? 0;
	const isPaid = meta?.tariffication?.paid ?? false;
	const topicId = meta?.automation?.topicId;

	const messageContentType = getMessageType(meta);
	const { isOpen } = useBotPaywallContext();
	const { isDemoUser } = useSession();

	const isUserMessage = useMemo(() => {
		return userId === sender;
	}, [userId, sender]);

	const isLastBotMessage = isLast && !isUserMessage;

	const { reportMessage } = useReportActionsContext();

	const ref = useRef<PopupActions | null>(null);
	const [dotsOpen, setDotsOpen] = useState(false);
	const [isForceStopPlayMedia, setForceStopPlayMedia] = useState(false);

	const topics = useTopics(botId);
	const { premiumTopics, cache } = usePremiumTopics(botId);
	const topic = useMemo(() => {
		if (showTopic) {
			return [...topics, ...premiumTopics, ...cache].find(
				(topic) => topic.Id === topicId
			);
		}

		return null;
	}, [cache, premiumTopics, showTopic, topicId, topics]);

	const paymentType = transformMessagePaymentType({
		type: meta?.tariffication?.type,
		cost,
		isPaid,
		isOwnMessage: isUserMessage,
	});

	const { paymentRequired, showPrice, action } = useMessageProcessing(
		paymentType,
		messageContentType,
		isPaid,
		cost,
		userId,
		messageId,
		bot,
		isMessageFromInfluencer
	);

	const actionHandled = useRef(false);

	const filteredAction = useCallback(() => {
		if (!actionHandled.current && action) {
			action();
		}
	}, [action]);

	const canReportMessage = useMemo(() => {
		return !isDemoUser && !paymentRequired && !isUserMessage;
	}, [isDemoUser, isUserMessage, paymentRequired]);

	const [reportSource, setReportSource] = useState<
		ReportMessageSource | undefined
	>();

	const trackReportMenuOpened = annals.report.useReportMenuOpened({
		messageId,
	});

	const trackReportButtonClicked = annals.report.useReportButtonClicked({
		messageId,
	});

	const reportMessageAction = useCallback(
		(source: ReportMessageSource) => {
			if (canReportMessage) {
				setReportSource(source);

				void trackReportMenuOpened({ source });

				setDotsOpen(true);
				actionHandled.current = true;
				ref.current?.open();
			}
		},
		[canReportMessage, trackReportMenuOpened]
	);

	useEffect(() => {
		if (isOpen) {
			ref.current?.close();
		}
	}, [isOpen]);

	const closePopup = useCallback(() => {
		ref.current?.close();
		setDotsOpen(false);
	}, []);

	const onReported = useCallback(() => {
		setForceStopPlayMedia(true);
	}, []);

	const openReport = useCallback(() => {
		ref.current?.close();
		setDotsOpen(false);
		if (reportSource) {
			void trackReportButtonClicked();
			reportMessage({
				botName: bot.name,
				message,
				source: reportSource,
				onReported,
			})();
		}
	}, [
		reportSource,
		trackReportButtonClicked,
		reportMessage,
		bot.name,
		message,
		onReported,
	]);

	const pickMessageContent = useMemo(() => {
		if (!message.tag && !message.id && !message.text) {
			// TODO: somehow we got an invalid message here, further investigation needed to find out where such message is generated.
			return;
		}

		if (messageContentType === 'video') {
			return (
				<VideoMessage
					message={message}
					userId={userId}
					botId={botId}
					action={filteredAction}
					showPrice={showPrice}
					showPaid={paymentRequired}
				/>
			);
		}
		if (messageContentType === 'image') {
			return (
				<ImageMessage
					message={message}
					action={action}
					botId={botId}
					paymentRequired={paymentRequired}
					showPrice={showPrice}
				/>
			);
		}
		if (messageContentType === 'gift' && meta?.giftPreview) {
			const links = message.meta?.mediaLinks as TMedia[];
			return paymentRequired ? (
				<PaidImageMessage
					message={message}
					media={links || []}
					showPrice={showPrice}
					action={filteredAction}
				/>
			) : (
				<GiftMessage links={meta.publicLinks} preview={meta.giftPreview} />
			);
		}
		if (messageContentType === 'text' && paymentRequired) {
			return <PaidTextMessage action={filteredAction} />;
		}
		if (messageContentType === 'audio') {
			return (
				<AudioMessageComponent
					message={message}
					botId={botId}
					action={filteredAction}
					showPaid={paymentRequired}
					needStopPlay={isForceStopPlayMedia}
				/>
			);
		}
		if (messageContentType === 'pack') {
			return (
				<SextingPackMessage
					message={message}
					botId={botId}
					onAction={filteredAction}
					showPaid={paymentRequired}
				/>
			);
		}

		if (message.text) {
			return (
				<>
					<TextMessage message={message} isOwn={isUserMessage} />
					{isLastBotMessage &&
						!meta?.hasWarning &&
						!!meta?.regeneration?.isAllowed &&
						!!meta?.tariffication?.regeneration && (
							<RegenerateButton
								messageId={message.id}
								userId={userId}
								recipientId={sender}
								regeneration={meta.tariffication?.regeneration}
								isMessageFromInfluencer={isMessageFromInfluencer}
							/>
						)}
				</>
			);
		}
	}, [
		userId,
		botId,
		isMessageFromInfluencer,
		filteredAction,
		isForceStopPlayMedia,
		isLastBotMessage,
		isUserMessage,
		messageContentType,
		action,
		message,
		meta,
		sender,
		paymentRequired,
		showPrice,
	]);

	const onContextMenu = useCallback(
		(e: MouseEvent<HTMLDivElement>) => {
			const target = e.target as HTMLElement;
			const isInDialog = target.closest('[role="dialog"]') !== null;

			if (
				target.id === 'regenerate-trigger' ||
				target?.getAttribute('class')?.includes('vjs') ||
				target?.getAttribute('role') === 'dialog' ||
				!!target?.getAttribute('tabindex') ||
				isInDialog
			) {
				return;
			}
			if (!dotsOpen) {
				reportMessageAction(ReportMessageSource.RightClick);
			}
			e.preventDefault();
			setDotsOpen(true);
		},
		[dotsOpen, reportMessageAction]
	);

	const content = useMemo(() => {
		if (!pickMessageContent) return;

		return (
			<Message
				id={message.tag}
				isMy={isUserMessage}
				onContextMenu={onContextMenu}
			>
				{pickMessageContent}
				{canReportMessage && messageContentType !== 'pack' && (
					<ReportPopup
						reportMessageAction={reportMessageAction}
						visible={dotsOpen}
					/>
				)}
			</Message>
		);
	}, [
		canReportMessage,
		dotsOpen,
		isUserMessage,
		messageContentType,
		onContextMenu,
		pickMessageContent,
		reportMessageAction,
		message.tag,
	]);

	return (
		<>
			{topic && (
				<TopicContainer>
					<Topic topic={topic} />
				</TopicContainer>
			)}
			{/*{showWarning ? <TermsWarning /> : null}*/}
			{content && (
				<ReportMessage
					ref={ref}
					trigger={content}
					openReport={openReport}
					closePopup={closePopup}
					onClose={() => setDotsOpen(false)}
				/>
			)}
			{/*Zero-sized element, this should not happen*/}
			{!topic && !content && <div />}
		</>
	);
};

export default memo(
	MessageItem,
	(prevProps, nextProps) =>
		prevProps.message.id === nextProps.message.id &&
		prevProps.message.tag === nextProps.message.tag &&
		prevProps.isLast === nextProps.isLast &&
		prevProps?.message.meta?.tariffication?.paid ===
			nextProps?.message.meta?.tariffication?.paid &&
		prevProps?.message.meta?.tariffication?.cost ===
			nextProps?.message.meta?.tariffication?.cost
);
